import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import type { WhatsAppConnectionPresentation } from '../../../types/whatsapp-connection-presentation.type';

interface WhatsAppConnectionActionsProps {
  /** Customer next step derived from persisted connection state. */
  state: WhatsAppConnectionPresentation;
  /** Whether the operator can verify stored setup. */
  canRecover: boolean;
  /** Whether recovery is already in flight. */
  recovering: boolean;
  /** Runs the parent's authenticated recovery mutation. */
  onRecover: () => void;
  /** Whether managed billing requires provider-assisted changes. */
  managed: boolean;
  /** Whether the operator can request disconnection assistance. */
  canManage: boolean;
}

const messageKeys: Record<
  Exclude<WhatsAppConnectionPresentation, 'healthy'>,
  TranslationKey
> = {
  retry_setup: 'settings.integrations.whatsapp.recovery.helper',
  reconnect: 'settings.integrations.whatsapp.recovery.reconnect',
  support: 'settings.integrations.whatsapp.recovery.support',
  meta_change: 'settings.integrations.whatsapp.recovery.metaChange',
  disconnect_pending: 'settings.integrations.whatsapp.offboarding.pending',
};

/** Separates connection recovery from provider-assisted billing/offboarding. */
export const WhatsAppConnectionActions: React.FC<
  WhatsAppConnectionActionsProps
> = ({ state, canRecover, recovering, onRecover, managed, canManage }) => {
  const { t } = useI18n();
  const needsAdministrator =
    !canRecover && (state === 'retry_setup' || state === 'reconnect');
  if (state === 'healthy' && !(managed && canManage)) return null;
  return (
    <div className="space-y-3">
      {state !== 'healthy' ? (
        <div className="flex flex-wrap items-center gap-3" role="status">
          <Text size="small" color="secondary">
            {t(
              needsAdministrator
                ? 'settings.integrations.whatsapp.recovery.adminRequired'
                : messageKeys[state]
            )}
          </Text>
          {state === 'retry_setup' && canRecover ? (
            <Button
              size="xs"
              appearance="outline"
              className="ml-auto shrink-0"
              disabled={recovering}
              onClick={onRecover}
            >
              {t(
                recovering
                  ? 'settings.integrations.whatsapp.recovery.pending'
                  : 'settings.integrations.whatsapp.recovery.action'
              )}
            </Button>
          ) : null}
        </div>
      ) : null}
      {managed && canManage ? (
        <Alert
          variant="info"
          size="sm"
          role="note"
          icon={
            <InformationCircleIcon className="h-5 w-5" aria-hidden="true" />
          }
          message={
            <div className="min-w-0 space-y-1">
              <Text size="small" weight="semibold">
                {t('settings.integrations.whatsapp.offboarding.title')}
              </Text>
              <Text size="small">
                {t('settings.integrations.whatsapp.offboarding.assisted')}
              </Text>
            </div>
          }
        />
      ) : null}
    </div>
  );
};
